import { useEffect, useState } from "react";
import type { DefinitionReadiness } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { loadDraft, type BuilderLoadDeps, type LoadedDraft } from "./builderLoad";
import type { DescribeDeps } from "./describe";
import { createDraftSession, type DraftSession, type DraftSnapshot } from "./draftSession";
import { createReadinessFeed, type ReadinessFeed } from "./readiness";

const token = (): string => getAccessToken() ?? "";

/**
 * The server calls the builder makes (KEY-560). Every one goes to the organisation the author signed in to: the authoring calls
 * never carry an org, and the writes the builder shares with the admin app are asked to leave it off.
 */
export function builderDeps(): BuilderLoadDeps {
  return {
    getDefinition: (id) => api.getDefinition(token(), id),
    getReadiness: (id) => api.getDefinitionReadiness(token(), id, "publish"),
    getAllowance: () => api.getAuthoringAllowance(token()),
    createDefinition: (model) => api.createDefinition(token(), model),
    copyTemplate: (templateId) => api.copyAuthoringTemplate(token(), templateId),
    setNoun: (id, noun) => api.setDefinitionSegmentNoun(token(), id, noun.singular, noun.plural, { homeOrg: true }),
  };
}

/**
 * The builder's calls plus an AI draft, which stops being waited for when `signal` aborts, and reading a document for one. The
 * upload never carries an org either, like every other authoring call.
 */
export function describeDeps(): DescribeDeps {
  return {
    ...builderDeps(),
    generate: (request, signal) => api.generateDefinitionAsync(token(), request, { signal }),
    extractDocument: (file) => api.extractSourceDocument(token(), file, null),
  };
}

/** The calls Launch and a test copy make: going live, trying it first, putting a test copy away, and the author's designs. */
export function authoringCalls() {
  return {
    goLive: (id: string) => api.goLive(token(), id),
    listMyDesigns: () => api.listMyDesigns(token()),
    // No audience: a dial somebody built is always tried by its author alone.
    sendForChecking: (id: string) => api.sendDefinitionForChecking(token(), id),
    revert: (id: string) => api.revertDefinitionToDraft(token(), id),
  };
}

export type DraftPhase =
  | { phase: "new" }
  | { phase: "loading" }
  | { phase: "ready"; draft: LoadedDraft; snapshot: DraftSnapshot; session: DraftSession; readiness: DefinitionReadiness }
  | { phase: "notFound" }
  | { phase: "trialEnded" }
  | { phase: "failed" };

/**
 * The design open in the builder, autosaved (KEY-560). No id means a new dial, which Start creates. Leaving the builder saves
 * whatever is still unsaved on the way out, and closing or reloading the tab with unsaved changes asks first. Readiness is read
 * again after every save, so it's current on whichever stage the author opens.
 */
export function useDraftSession(id: string | undefined): DraftPhase {
  const [state, setState] = useState<DraftPhase>(id ? { phase: "loading" } : { phase: "new" });

  useEffect(() => {
    if (!id) {
      setState({ phase: "new" });
      return;
    }
    setState({ phase: "loading" });

    let cancelled = false;
    let session: DraftSession | null = null;
    let feed: ReadinessFeed | null = null;
    const deps = builderDeps();

    loadDraft(deps, id)
      .then((result) => {
        if (cancelled) return;
        if (result.kind !== "loaded") {
          setState({ phase: result.kind });
          return;
        }
        const { draft } = result;
        const readinessFeed = createReadinessFeed(
          () => deps.getReadiness(id),
          (readiness) => {
            if (!cancelled) setState((previous) => (previous.phase === "ready" ? { ...previous, readiness } : previous));
          },
        );
        feed = readinessFeed;
        const opened = createDraftSession(
          { model: draft.model, noun: draft.noun },
          {
            save: (model) => api.updateDefinition(token(), id, model, { homeOrg: true }),
            saveNoun: (noun) => deps.setNoun(id, noun),
            readLifecycle: async () => (await api.getDefinition(token(), id)).lifecycle,
            onChange: (snapshot) => {
              if (cancelled) return;
              if (snapshot.ended) {
                setState({ phase: snapshot.ended === "trialEnded" ? "trialEnded" : "notFound" });
                return;
              }
              readinessFeed.saved(snapshot.saves);
              setState((previous) => (previous.phase === "ready" ? { ...previous, snapshot } : previous));
            },
          },
        );
        // A design out for checking, or already live, can't be edited here: it opens locked, as a refused save would leave it.
        if (draft.lifecycle === "Checking" || draft.lifecycle === "Published") opened.lock(draft.lifecycle);
        session = opened;
        setState({ phase: "ready", draft, snapshot: opened.snapshot(), session: opened, readiness: draft.readiness });
      })
      .catch(() => {
        if (!cancelled) setState({ phase: "failed" });
      });

    return () => {
      cancelled = true;
      feed?.stop();
      if (session) {
        // Leaving by the browser's Back, or to another dial: save what's left, then stop.
        const leaving = session;
        void leaving.flush().finally(() => leaving.dispose());
      }
    };
  }, [id]);

  const unsaved = state.phase === "ready" && state.snapshot.unsaved;
  useEffect(() => {
    if (!unsaved) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  return state;
}
